// Product analytics for the flyover / Trail Trailer share sheet.
//
// The server only ever sees the *render* (GenerateFlyoverVideo / GenerateHikeTrailer), never what
// happened after it: a video can render, be watched, and never be shared, and nothing downstream can
// tell Instagram from Copy link. `target` is the breakdown the whole event exists to produce — which
// platform a finished video actually goes to.
//
// Every name and wire string here is a contract with the apps
// (`modules/flyovers/analytics/FlyoverShareAnalytics.kt` on Android,
// `Modules/ShareRecordingVideo/Analytics/FlyoverShareAnalyticsEvent.swift` on iOS): all three feed one
// PostHog project, so a drifting value splits a breakdown in two rather than failing anywhere.
//
// Volume is one event per deliberate tap on a share tile — a handful per share at most.

import { isHikeShare, type ShareSubject } from './trailerShare';

export const FLYOVER_SHARE_TARGET_EVENT = 'Flyover Share Target Tapped';

/** The share tiles the ready step offers. Keys match `shareTargets` in FlyoverShare.vue. */
export type ShareTargetKey =
  | 'facebook'
  | 'facebookReel'
  | 'instagram'
  | 'instagramStories'
  | 'messenger'
  | 'whatsapp'
  | 'email'
  | 'more'
  | 'link'
  | 'saveVideo';

/**
 * Tile key → wire value. snake_case, and identical to the apps' own mapping, so one PostHog
 * breakdown answers "where do shared videos go" across web, Android and iOS at once.
 */
export const SHARE_TARGET_WIRE: Record<ShareTargetKey, string> = {
  facebook: 'facebook',
  facebookReel: 'facebook_reel',
  instagram: 'instagram',
  instagramStories: 'instagram_stories',
  messenger: 'messenger',
  whatsapp: 'whatsapp',
  email: 'email',
  more: 'more',
  link: 'link',
  saveVideo: 'save_video',
};

/** Recording flyover vs hike Trail Trailer. */
export type ShareKind = 'flyover' | 'trailer';

/**
 * Where the share wizard was opened from. Shared vocabulary across the three platforms; web only
 * ever emits the ones below, the apps add their own (`recordings_list`, `share_rich_card`).
 *
 * `hike_overview`, `comment` and `my_flyovers_player` are also the fullscreen players' Share button (see readyVideoShare.ts), which opens the
 * wizard straight onto an already-rendered video: the hike page's trailer, a comment's flyover tile,
 * and My Flyovers' own player (its row share icon stays `my_flyovers`). A recording page's player
 * reports `recording_overview`, the same as that page's Generate button — same screen, same bucket.
 * `hike_overview` / `hikes_list` are the "Hike trailer" share option on the hike page and on a
 * hikes-list row.
 * `unknown` is the fallback for an entry point that forgot to declare itself — it shows up as its
 * own bucket rather than silently inflating a real source.
 */
export type ShareSource =
  | 'recording_overview'
  | 'trail_3d_preview'
  | 'my_flyovers'
  | 'hike_post_save_prompt'
  | 'hike_overview'
  | 'comment'
  | 'my_flyovers_player'
  | 'hikes_list'
  | 'unknown';

const KNOWN_SOURCES: readonly string[] = [
  'recording_overview',
  'trail_3d_preview',
  'my_flyovers',
  'hike_post_save_prompt',
  'hike_overview',
  'comment',
  'my_flyovers_player',
  'hikes_list',
];

/** Keeps `source` low-cardinality: anything the caller didn't declare reads as `unknown`. */
export function normalizeShareSource(source?: string | null): ShareSource {
  const raw = (source ?? '').trim();
  return (KNOWN_SOURCES.includes(raw) ? raw : 'unknown') as ShareSource;
}

/** A config the share wizard was opened with, as far as this module cares. */
export interface AnalyticsShareConfig extends ShareSubject {
  /**
   * Explicit subject kind. Only needed where the wizard opens straight onto an already-rendered
   * video and therefore carries no subject id at all (the My-flyovers list): there `hikeId` is
   * deliberately absent — setting it would change which mutation the wizard would call — so the
   * caller states the kind instead. Everywhere else the subject ids decide.
   */
  kind?: ShareKind | null;
  source?: string | null;
}

/** Recording flyover or hike trailer: the caller's explicit kind wins, else the subject ids decide. */
export function shareKind(cfg: AnalyticsShareConfig | null | undefined): ShareKind {
  if (cfg?.kind === 'trailer' || cfg?.kind === 'flyover') return cfg.kind;
  return isHikeShare(cfg) ? 'trailer' : 'flyover';
}

export interface FlyoverShareTargetProps {
  target: string;
  kind: ShareKind;
  format: 'vertical' | 'horizontal';
  source: ShareSource;
}

/**
 * The event payload. `format` is the shape of the video actually being shared (a reused render keeps
 * its own aspect, which can differ from the format last picked on the format step), so the property
 * describes what landed on the platform rather than what was requested.
 */
export function flyoverShareTargetProps(
  cfg: AnalyticsShareConfig | null | undefined,
  target: ShareTargetKey,
  videoIsVertical: boolean,
): FlyoverShareTargetProps {
  return {
    target: SHARE_TARGET_WIRE[target],
    kind: shareKind(cfg),
    format: videoIsVertical ? 'vertical' : 'horizontal',
    source: normalizeShareSource(cfg?.source),
  };
}

/** Fire-and-forget; never let a missing analytics shim break a share. */
export function trackFlyoverShareTarget(
  cfg: AnalyticsShareConfig | null | undefined,
  target: ShareTargetKey,
  videoIsVertical: boolean,
): void {
  try {
    window.mixpanel?.track(
      FLYOVER_SHARE_TARGET_EVENT,
      flyoverShareTargetProps(cfg, target, videoIsVertical),
    );
  } catch {
    /* analytics must never break the share */
  }
}
